/**
 * Shared logic for INKO (classic + daily).
 *
 * Public API:
 *   - normalize(str)              → diacritics-insensitive lowercase alnum
 *   - isCorrect(guess, aliases)   → exact match after normalization
 *   - isCorrectFuzzy(guess, aliases) → typo-tolerant (Levenshtein ≤ 2 for guess≥5 chars)
 *   - buildOptions(correct, all)  → 4 Easy-mode options (same-era distractors)
 *   - highlightClue(text)         → returns array of {text, bold} segments
 *   - autocompleteSearch(prefix, allPlayers, limit) → up to `limit` player names
 *   - pacingConfig(mode)          → {round_duration_s, clue_interval_s, score_table}
 */

// ---- Normalize --------------------------------------------------------

/**
 * Special latin-extended → ASCII mapping for characters that NFD leaves as
 * a single base glyph (so the diacritic-strip regex misses them).
 */
const TRANSLIT = {
    "ð": "d", "Ð": "d", "þ": "th", "Þ": "th",
    "ø": "o", "Ø": "o", "æ": "ae", "Æ": "ae",
    "ł": "l", "Ł": "l", "ß": "ss",
};

/** Lowercase, transliterate special glyphs, strip diacritics, keep [a-z0-9]. */
export const normalize = (s) => {
    if (!s) return "";
    let out = "";
    for (const ch of s) out += TRANSLIT[ch] ?? ch;
    return out
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
};

// ---- Matching ---------------------------------------------------------

const levenshtein = (a, b) => {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
        const curr = [i];
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            curr.push(Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost));
        }
        prev = curr;
    }
    return prev[b.length];
};

/** Exact match after normalization. */
export const isCorrect = (guess, aliases) => {
    const n = normalize(guess);
    if (!n) return false;
    return aliases.some((a) => normalize(a) === n);
};

/**
 * Typo-tolerant match. Uses Levenshtein distance with a threshold that
 * scales with alias length:
 *   length ≤ 4 → exact only
 *   length 5-8 → distance ≤ 1
 *   length ≥ 9 → distance ≤ 2
 */
export const isCorrectFuzzy = (guess, aliases) => {
    const g = normalize(guess);
    if (!g) return false;
    for (const alias of aliases) {
        const a = normalize(alias);
        if (!a) continue;
        if (a === g) return true;
        const threshold = a.length >= 9 ? 2 : a.length >= 5 ? 1 : 0;
        if (threshold > 0 && Math.abs(a.length - g.length) <= threshold) {
            if (levenshtein(a, g) <= threshold) return true;
        }
    }
    return false;
};

// ---- Options builder --------------------------------------------------

const shuffleArr = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

// Deterministic string hash → 32-bit unsigned int (for seeded shuffles).
const hashString = (str) => {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    return h;
};

// Small deterministic PRNG (mulberry32).
const mulberry32 = (seed) => {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6D2B79F5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
};

/** Seeded Fisher-Yates shuffle — same seed always returns same order. */
const shuffleArrSeeded = (arr, seed) => {
    const rng = mulberry32(seed);
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

/**
 * Easy-mode / Medium-mode options.
 *   difficulty="easy"   → distraktory ze stejné éry (může se lišit pozice)
 *   difficulty="medium" → distraktory ze stejné éry A stejné pozice — mnohem těžší
 * Fallback řetěz: same-era-and-position → same-era → any.
 */
// Extract surname (last space-separated token) from a full name/answer.
const surnameOf = (raw) => {
    if (!raw) return "";
    return String(raw).trim().split(/\s+/).slice(-1)[0] || "";
};

// Cheap surname-similarity score: 0..1 based on shared letters + length gap.
// Not linguistic — just enough to prefer "Salah / Selah / Saliba" over
// "Salah / Kane / Rice" when we have the choice.
const surnameSimilarity = (a, b) => {
    a = a.toLowerCase(); b = b.toLowerCase();
    if (!a || !b || a === b) return 0;
    // Same first letter → +0.3, same last letter → +0.2, same length ±1 → +0.2,
    // shared letters ratio → +0.3 max.
    let s = 0;
    if (a[0] === b[0]) s += 0.3;
    if (a[a.length - 1] === b[b.length - 1]) s += 0.2;
    if (Math.abs(a.length - b.length) <= 1) s += 0.2;
    const setA = new Set(a); const setB = new Set(b);
    let shared = 0;
    for (const ch of setA) if (setB.has(ch)) shared++;
    s += 0.3 * (shared / Math.max(setA.size, setB.size));
    return s;
};

export const buildOptions = (correctPlayer, allPlayers, difficulty = "easy", allManagers = []) => {
    // Manager rare-card: distractors are OTHER managers (else it would be
    // obvious which option is the manager).
    if (correctPlayer.entity_type === "manager") {
        const managerPool = allManagers.filter((m) => m.id !== correctPlayer.id);
        let distractors = shuffleArr(managerPool).slice(0, 3);
        if (distractors.length < 3) {
            // Fallback to player-era peers if not enough managers available
            const filler = shuffleArr(
                allPlayers.filter((p) => p.era === correctPlayer.era && p.id !== correctPlayer.id)
            ).slice(0, 3 - distractors.length);
            distractors = [...distractors, ...filler];
        }
        return shuffleArr([correctPlayer, ...distractors]).map((p) => p.answer);
    }

    const wantPosition = difficulty === "medium";
    let pool = allPlayers.filter(
        (p) =>
            p.id !== correctPlayer.id &&
            p.era === correctPlayer.era &&
            (!wantPosition || p.position_hint === correctPlayer.position_hint)
    );

    // Surname-similarity weighting: pick 2 similar-surname distractors + 1
    // random. User feedback — "at se mohou plést v příjmení". Only kicks in
    // when the pool has enough options; otherwise falls back to shuffle.
    const correctSurname = surnameOf(correctPlayer.answer || correctPlayer.name);
    let distractors = [];
    if (pool.length >= 4 && correctSurname) {
        const ranked = pool
            .map((p) => ({ p, score: surnameSimilarity(correctSurname, surnameOf(p.answer || p.name)) }))
            .sort((a, b) => b.score - a.score);
        // Top-8 candidates by similarity → shuffle → take 2 (adds unpredictability).
        const topSim = shuffleArr(ranked.slice(0, 8).map((r) => r.p)).slice(0, 2);
        const rest = shuffleArr(pool.filter((p) => !topSim.some((t) => t.id === p.id)));
        distractors = [...topSim, rest[0]].filter(Boolean);
    } else {
        distractors = shuffleArr(pool).slice(0, 3);
    }

    if (distractors.length < 3 && wantPosition) {
        // Fallback: relax position, keep era
        const relaxed = allPlayers.filter(
            (p) =>
                p.id !== correctPlayer.id &&
                p.era === correctPlayer.era &&
                !distractors.some((d) => d.id === p.id)
        );
        distractors = [...distractors, ...shuffleArr(relaxed).slice(0, 3 - distractors.length)];
    }
    if (distractors.length < 3) {
        const extra = shuffleArr(
            allPlayers.filter(
                (p) =>
                    p.id !== correctPlayer.id &&
                    !distractors.some((d) => d.id === p.id)
            )
        ).slice(0, 3 - distractors.length);
        distractors = [...distractors, ...extra];
    }
    return shuffleArr([correctPlayer, ...distractors]).map((p) => p.answer);
};

// ---- Highlight keywords ----------------------------------------------

// Match seasons like 2019/20, 2021/22 (2-digit or 4-digit second half).
const RE_SEASON = /\b(20\d{2}\/\d{2,4})\b/g;
// Standalone 4-digit years 1990-2029
const RE_YEAR = /\b(19[89]\d|20[0-2]\d)\b/g;
// FPL / football numeric facts with units.
const RE_NUMBERS = /\b(\d{1,3}(?:\s?[gGaA])\b|\d{1,3}\s?gólů|\d{1,3}\s?asistencí|\d{1,4}\s?bodů|\d{1,3}\s?bodů|\d{1,4}\s?FPL\s?bodů|£\d{1,3}(?:\.\d+)?m|\d{1,4}M?\s?€|\d{1,4}\s?milion[uů]|\d{1,3}\s?%|\d{1,3}\s?cm|\d{1,2}\s?čist(?:ých)?\s?kont|clean\s?sheet[su]?)/g;
// Iconic trophies / awards.
const RE_TROPHIES = /(Golden Boot|Golden Glove|PFA(?:\s+Player of the Year)?|FWA(?:\s+Footballer of the Year)?|Ballon d'Or|Player of the Year|Player of the Season|Premier League(?!\s+Fantasy)|Ligy mistr[uů]?|Champions League|MS 20\d{2}|EURO 20\d{2}|Copa America|African Cup of Nations|Africa Cup|FA\s?Cup|EFL\s?Cup|Community\s?Shield|Europa League|Confederations Cup)/g;
// Common numeric words (rekord, hattrick, ownership).
const RE_KEYWORDS = /\b(rekord(?:\w*)?|hattrick(?:\w*)?|kapitán(?:\w*)?|template|ownership\s+\d+\s?%|invincibles|clean\s+sheet[su]?)\b/gi;
// Nationality adjectives (Czech).
const RE_NATIONALITY = /\b(Anglický|Skotský|Velšský|Irský|Severoirský|Francouzský|Německý|Španělský|Portugalský|Belgický|Nizozemský|Italský|Brazilský|Argentinský|Uruguayský|Egyptský|Nigerijský|Ghánský|Ivorijský|Pobřeží\s+Slonoviny|Senegalský|Norský|Švédský|Dánský|Islandský|Chorvatský|Srbský|Polský|Ruský|Ukrajinský|Kolumbijský|Chilský|Ekvádorský|Peruánský|Mexický|Korejský|Jihokorejský|Japonský|Australský|Novozélandský|Kamerunský|Ganský|Alžírský|Marocký|Tuniský|Řecký|Turecký|Rumunský|Slovenský|Český|Rakouský|Švýcarský|Bosenský|Albánský|Ekvádorský|Guinejský)\b/g;
// Position words (football roles).
const RE_POSITION = /\b(brankář|obránce|stoper|krajní\s+obránce|levý\s+bek|pravý\s+bek|záložník|středopolař|defenzivní\s+záložník|ofenzivní\s+záložník|křídel(?:ník|o)|útočník|hrot|centrální\s+útočník|středový\s+útočník|hráč|kapitán|midfielder|striker|winger|defender|goalkeeper)\b/gi;
// PL club names (English + Czech aliases).
const RE_CLUB = /\b(Arsenal|Chelsea|Liverpool|Man\s*(?:United|City|Utd)|Manchester\s*(?:United|City|Utd)|Tottenham|Spurs|Newcastle|Everton|Aston\s*Villa|West\s*Ham|Leeds|Leicester|Southampton|Wolves|Wolverhampton|Fulham|Crystal\s*Palace|Bournemouth|Brighton|Brentford|Ipswich|Nottingham\s*Forest|Sheffield\s*(?:United|Wednesday)|Norwich|Watford|Burnley|Stoke|Sunderland|Blackburn|Middlesbrough|Reading|Cardiff|Swansea|Hull|Blackpool|Real\s*Madrid|Barcelona|Bayern(?:\s*Mnichov)?|PSG|Paris\s*Saint-Germain|Juventus|AC\s*Milán|Inter(?:\s*Milán)?|Roma|Napoli|Atl[eé]tico\s*Madrid|Ajax|Feyenoord|PSV|Sporting(?:\s*CP)?|Porto|Benfica|Frankfurt|Eintracht(?:\s*Frankfurt)?|Dortmund|Borussia(?:\s*Dortmund)?|Leverkusen|Anfield|Emirates|Etihad|Old\s*Trafford|Bridge|Ligue\s*1|Bundesliga|La\s*Liga|Serie\s*A|MLS)\b/g;

const HL_PATTERNS = [
    RE_SEASON, RE_NUMBERS, RE_TROPHIES, RE_KEYWORDS,
    RE_NATIONALITY, RE_POSITION, RE_CLUB, RE_YEAR,
];

// Czech + English stopwords / glue words / too-generic tokens for FPL clues.
// Anything not in this set (and length >= 3) becomes a chip.
const STOPWORDS = new Set([
    // Czech prepositions, conjunctions, particles
    "a","i","o","u","v","k","s","z","ve","ze","ke","se","na","po","do","od","za","při",
    "před","pod","nad","přes","proti","kolem","podle","mimo","mezi","kvůli","díky",
    "než","už","již","také","též","jen","pouze","velmi","hodně","více","méně","tak",
    "však","ale","nebo","anebo","protože","aby","když","pokud","jako","který","která",
    "které","kteří","kterou","kterým","kterých","kterýmž","jehož","jejíž","jenž",
    "to","ta","ten","toho","tomu","tím","této","tomto","tímto","tuto","tímto","tohoto",
    "tomuto","tato","této","tuto","tato","tato","této","těch","těmto","těmi",
    "je","jsou","jsem","jsi","jste","jsme","byl","byla","bylo","byli","byly","být","bude",
    "byly","byli","být","budou","bych","bys","by","byste","bychom","byste",
    "se","si","svými","svého","svým","své","svá","svých","svými","svůj","svoji","svoji",
    "svem","svém","své","svého","svými","svých","svou","svým","svými","svých",
    "mu","mi","ho","ji","je","jich","nim","ním","ni","ním","něj","nich","jich","jejich",
    "můj","tvůj","jeho","její","náš","váš","jejich","můj","můj","můj","naší","naším",
    "co","kdo","kde","kdy","jak","proč","kolik","který","která","které",
    "než","poté","přesto","stále","opět","znovu","dále","navíc","tedy","proto","totiž",
    "což","aniž","jenže","ani","buď","hlavně","právě","zejména","obvykle","často",
    "doby","době","dobu","dobou","dobách","doby",
    "dnes","dnes","dnes","nyní","tehdy","tehdy","kdysi","často","občas","vždy","nikdy",
    // Czech "hráč / sezóna / klub / tým" – too generic to be helpful as isolated chip
    "hráč","hráče","hráči","hráčů","hráčem",
    "klub","klubu","klubem","klubu","klubů","klubem",
    "tým","týmu","týmem","týmy","týmů",
    "sezóna","sezony","sezoně","sezónu","sezónou","sezón","sezónách","sezónami",
    "sezóně","sezona","sezoně","sezonou",
    "rok","roku","roky","let","letech","letech","letech","roku","roce",
    "gól","gólů","gólem","gólech","góly","gólu",
    "asistence","asistencí","asistencemi","asistenci",
    "bod","bodu","body","bodů","bodech","bodem",
    // English filler
    "the","a","an","of","in","on","and","or","for","to","with","at","by","from","is",
    "are","was","were","be","been","being","this","that","these","those","it","its",
    "as","but","not","no","yes","he","she","they","we","you","i","his","her","their",
]);

/**
 * Extract ONLY the essentials from a clue as an array of chip strings.
 * Strategy:
 *   1. Collect regex-matched high-value tokens (seasons, years, clubs, trophies …)
 *      and their character ranges.
 *   2. From the leftover text, tokenize into words / short phrases and keep only
 *      content words (length ≥ 3, not in STOPWORDS).
 *   3. De-duplicate case-insensitively, preserving original casing & order.
 * If the sentence yields nothing at all, returns [] (caller can fall back).
 */
export const extractKeywords = (text) => {
    if (!text) return [];

    // Step 1 — regex hits with their spans
    const spans = []; // [start, end, matchedText]
    for (const re of HL_PATTERNS) {
        re.lastIndex = 0;
        let m;
        while ((m = re.exec(text)) !== null) {
            const s = m[0].trim();
            if (s.length >= 2) spans.push([m.index, m.index + m[0].length, s]);
        }
    }

    // Sort & merge overlapping spans
    spans.sort((a, b) => a[0] - b[0]);
    const merged = [];
    for (const sp of spans) {
        if (merged.length && sp[0] <= merged[merged.length - 1][1]) {
            const last = merged[merged.length - 1];
            last[1] = Math.max(last[1], sp[1]);
            last[2] = text.slice(last[0], last[1]).trim();
        } else {
            merged.push([...sp]);
        }
    }

    // Step 2 — build leftover text (mask matched ranges with spaces)
    const chars = text.split("");
    for (const [s, e] of merged) {
        for (let i = s; i < e; i++) chars[i] = " ";
    }
    const leftover = chars.join("");

    // Ordered list of {index, chip}
    const items = merged.map(([s, , t]) => ({ index: s, chip: t }));

    // Step 3 — content words from leftover
    // Match word tokens including czech letters, digits and hyphens/apostrophes.
    const wordRe = /[\p{L}0-9]+(?:[-'][\p{L}0-9]+)*/gu;
    let wm;
    while ((wm = wordRe.exec(leftover)) !== null) {
        const raw = wm[0];
        const lc = raw.toLowerCase();
        if (raw.length < 3) continue;
        if (STOPWORDS.has(lc)) continue;
        // Skip pure numbers < 3 chars already filtered by length; but skip years already
        // captured (rare double dip since we masked them).
        items.push({ index: wm.index, chip: raw });
    }

    items.sort((a, b) => a.index - b.index);

    // Dedupe (case-insensitive), preserve first casing
    const seen = new Set();
    const out = [];
    for (const { chip } of items) {
        const key = chip.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(chip);
    }
    return out;
};

/**
 * Legacy: return an array of {text, bold} segments (bold-inline highlight).
 * Kept for backward compatibility / future dual-render mode.
 */
export const highlightClue = (text) => {
    if (!text) return [];
    const intervals = [];
    for (const re of HL_PATTERNS) {
        re.lastIndex = 0;
        let m;
        while ((m = re.exec(text)) !== null) {
            intervals.push([m.index, m.index + m[0].length]);
        }
    }
    if (intervals.length === 0) return [{ text, bold: false }];
    intervals.sort((a, b) => a[0] - b[0]);
    const merged = [];
    for (const [s, e] of intervals) {
        if (merged.length && s <= merged[merged.length - 1][1]) {
            merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
        } else {
            merged.push([s, e]);
        }
    }
    const segs = [];
    let cur = 0;
    for (const [s, e] of merged) {
        if (s > cur) segs.push({ text: text.slice(cur, s), bold: false });
        segs.push({ text: text.slice(s, e), bold: true });
        cur = e;
    }
    if (cur < text.length) segs.push({ text: text.slice(cur), bold: false });
    return segs;
};

// ---- Autocomplete ----------------------------------------------------

/**
 * Suggest player names as user types. Matches against `answer` and all
 * `aliases`. Ranks by (a) prefix match on any alias, (b) substring match.
 * Returns up to `limit` unique player answers.
 */
export const autocompleteSearch = (prefix, allPlayers, limit = 5) => {
    const p = normalize(prefix);
    if (p.length < 2) return [];
    const prefixHits = [];
    const substringHits = [];
    for (const player of allPlayers) {
        const targets = [player.answer, ...(player.aliases || [])].map(normalize);
        let matched = null;
        for (const t of targets) {
            if (!t) continue;
            if (t.startsWith(p)) {
                matched = "prefix";
                break;
            }
            if (t.includes(p)) {
                matched = matched || "substring";
            }
        }
        if (matched === "prefix") prefixHits.push(player.answer);
        else if (matched === "substring") substringHits.push(player.answer);
    }
    // Dedup preserving order
    const seen = new Set();
    const out = [];
    for (const name of [...prefixHits, ...substringHits]) {
        if (seen.has(name)) continue;
        seen.add(name);
        out.push(name);
        if (out.length >= limit) break;
    }
    return out;
};

// ---- Pacing config ---------------------------------------------------

/**
 * Reading modes:
 *   - classic   : 5 s per clue, 30 s round, no highlight
 *   - slow      : 10 s per clue, 60 s round, no highlight
 *   - highlight : 5 s per clue, 30 s round, keywords bolded
 *
 * Score table always has 6 tiers — one per clue reveal.
 */
export const READING_MODES = ["slow", "classic", "keywords", "porno"];

export const READING_MODE_LABELS = {
    slow: "Slow",
    classic: "Middle",
    keywords: "Thunder",
    porno: "Porno",
};

export const pacingConfig = (mode, difficulty = null) => {
    // Fucking Legend (hard + porno) gets a tighter clock — same 6 clues but
    // condensed into 24 s instead of 30 s (4 s per clue instead of 5).
    // This is the only tier that overrides the reading-mode default.
    if (mode === "porno" && difficulty === "hard") {
        return { round_duration_s: 24, clue_interval_s: 4, keywords: false, porno: true };
    }
    if (mode === "slow") {
        return { round_duration_s: 60, clue_interval_s: 10, keywords: false, porno: false };
    }
    if (mode === "keywords" || mode === "highlight" /* legacy */) {
        return { round_duration_s: 30, clue_interval_s: 5, keywords: true, porno: false };
    }
    if (mode === "porno") {
        return { round_duration_s: 30, clue_interval_s: 5, keywords: false, porno: true };
    }
    return { round_duration_s: 30, clue_interval_s: 5, keywords: false, porno: false };
};

export const SCORE_TABLE = [100, 80, 60, 40, 20, 10];

// ---- Difficulty tiers (single UX abstraction over mode + reading_mode) --

/**
 * INKO now presents 6 named tiers instead of two independent pickers.
 * Order = difficulty ramp; each tier owns a `{mode, reading_mode}` combo
 * that determines pacing, distractor rules, and the leaderboard bucket.
 *
 *   1. noob            (easy   + slow)
 *   2. padawan         (medium + classic/middle)
 *   3. thunder         (medium + keywords/thunder)
 *   4. porno           (medium + porno)
 *   5. expert          (hard   + keywords/thunder)
 *   6. fucking legend  (hard   + porno)
 */
export const INKO_TIERS = [
    {
        id: "noob",           label: "Noob",
        mode: "easy",   reading_mode: "slow",
        color: "fa-mint",     order: 1,
        desc: "Easy · Slow · 60 s / hráče · 4 možnosti",
    },
    {
        id: "padawan",        label: "Padawan",
        mode: "medium", reading_mode: "classic",
        color: "fa-cyan",     order: 2,
        desc: "Medium · Middle · 30 s / hráče · plné věty",
    },
    {
        id: "thunder",        label: "Thunder",
        mode: "medium", reading_mode: "keywords",
        color: "fa-yellow",   order: 3,
        desc: "Medium · Thunder · 30 s · jen klíčová slova",
    },
    {
        id: "porno",          label: "Porno",
        mode: "medium", reading_mode: "porno",
        color: "orange",      order: 4,
        desc: "Medium · Porno · 30 s · reálné GW momenty",
    },
    {
        id: "expert",         label: "Expert",
        mode: "hard",   reading_mode: "keywords",
        color: "red",         order: 5,
        desc: "Hard · Thunder · napiš jméno · jen klíčová slova",
    },
    {
        id: "fucking_legend", label: "Fucking Legend",
        mode: "hard",   reading_mode: "porno",
        color: "fuchsia",     order: 6,
        desc: "Hard · Porno · 24 s · nová indície každé 4 s · napiš jméno",
    },
];

/**
 * Resolve the tier that a (mode, reading_mode) combo belongs to. Returns
 * `null` when the combo doesn't map onto a canonical tier (e.g. legacy
 * combos from before the tier UI was introduced). Callers should render a
 * "custom combo" fallback in that case.
 */
export const getTier = (mode, reading_mode) => {
    if (!mode || !reading_mode) return null;
    return INKO_TIERS.find((t) => t.mode === mode && t.reading_mode === reading_mode) || null;
};

/**
 * Resolve tier by its id string.
 */
export const getTierById = (id) => INKO_TIERS.find((t) => t.id === id) || null;

// ---- Manager / entity-type helpers ------------------------------------

/**
 * Resolve the cross-fantasy profile for a player. Accepts both the current
 * dataset field name (`cross_fantasy_profile_phase1`) and the future/canonical
 * name (`cross_fantasy_profile`) — the ingest pipeline renamed the field
 * between phases and we don't want the UI to silently drop the story.
 */
export const isManager = (player) => player && player.entity_type === "manager";

export const getCrossFantasyProfile = (player) => {
    if (!player) return null;
    return player.cross_fantasy_profile || player.cross_fantasy_profile_phase1 || null;
};

/**
 * Return a short, UI-friendly summary of the cross-fantasy story. Combines
 * the profile's `story_summary` with the first verified moment's `quiz_hook`
 * when available.
 */
export const getCrossFantasyLore = (player) => {
    const cf = getCrossFantasyProfile(player);
    if (!cf) return null;
    const summary = cf.story_summary || cf.manager_perception || "";
    const firstMoment = (cf.verified_or_story_moments || [])[0];
    const hook = firstMoment?.quiz_hook || "";
    const competitions = Array.isArray(cf.competitions) ? cf.competitions : [];
    return {
        summary,
        hook,
        competitions,
        moment: firstMoment || null,
    };
};


export const MANAGER_MULTIPLIER = 2;      // rare card 2× points
export const HARD_EXTRA_MULTIPLIER = 1.25; // Hard mode bonus 1.25×
export const PAIN_MULTIPLIER = 1.5;        // FPL Pain 1.5×
export const LEGENDARY_PAIN_MULTIPLIER = 2; // Legendary Pain 2× (overrides base pain)
export const CROSS_FANTASY_MULTIPLIER = 1.25; // Cross-fantasy story 1.25×

/**
 * Score for a correct guess with entity- and mode-aware multipliers.
 *   base = SCORE_TABLE[cluesRevealed-1]
 *   × 2    if manager rare card
 *   × 2    if legendary FPL Pain
 *   × 1.5  if regular FPL Pain (high/medium severity)
 *   × 1.25 if cross-fantasy story (subset of classic_player)
 *   × 1.25 if mode === "hard"
 * Multipliers stack (legendary pain × hard = 2 × 1.25 = 2.5×).
 * Always returns a rounded integer.
 */
export const computeCorrectScore = (player, mode, cluesRevealed) => {
    const base = SCORE_TABLE[Math.min(cluesRevealed - 1, SCORE_TABLE.length - 1)] || 0;
    let s = base;
    if (isManager(player)) {
        s *= MANAGER_MULTIPLIER;
    } else if (isPain(player)) {
        s *= player.severity === "legendary" ? LEGENDARY_PAIN_MULTIPLIER : PAIN_MULTIPLIER;
    } else if (getCrossFantasyProfile(player)) {
        s *= CROSS_FANTASY_MULTIPLIER;
    }
    if (mode === "hard") s *= HARD_EXTRA_MULTIPLIER;
    return Math.round(s);
};

// ---- Bonuses & Titles (Phase 2 & 3) -----------------------------------

/**
 * Per-round bonus points earned on top of the base score. Applied ONLY
 * when the round ends in a correct guess. Returned as an array of
 * `{ id, label, points, icon }` so the UI can render individual chips.
 *
/**
 * Streak bonus tables (Phase 2 & 3 brief, graduated variant).
 *
 *   early_chain      — chain of `correct-by-clue-2` rounds:  2 → 25 b, 3 → 50, 4 → 75, 5+ → 100
 *   first_clue_chain — chain of `correct-by-clue-1` rounds:  2 → 50 b, 3 → 125, 4+ → 250
 *
 * `computeStreakBonus(chainType, chainLen)` returns the CURRENT round's bonus
 * (i.e. the reward earned by extending the chain to `chainLen`).
 */
export const computeStreakBonus = (chainType, chainLen) => {
    if (chainType === "early_chain") {
        if (chainLen >= 5) return 100;
        if (chainLen === 4) return 75;
        if (chainLen === 3) return 50;
        if (chainLen === 2) return 25;
        return 0;
    }
    if (chainType === "first_clue_chain") {
        if (chainLen >= 4) return 250;
        if (chainLen === 3) return 125;
        if (chainLen === 2) return 50;
        return 0;
    }
    return 0;
};

/**
 * Per-round bonus points earned on top of the base score. Applied ONLY
 * when the round ends in a correct guess.
 *
 * Accepts a `chainState = { earlyChain, firstClueChain }` snapshot from the
 * parent so bonuses can be graduated instead of flat. When omitted, falls
 * back to flat first-hit bonuses (backward compat for tests).
 *
 * Returned bonuses:
 *   • FIRST_CLUE_CHAIN — extends when correct on clue #1               (graduated 50 → 250)
 *   • EARLY_CHAIN      — extends when correct on clue ≤ 2, non-first  (graduated 25 → 100)
 *   • TOUCHLINE_SNIPE  — correct on a Rare Manager card                (+100 b)
 *   • DEATH_MODE_SURVIVOR — correct on an FPL Pain card                (+50 b legendary / +30 b other)
 *   • EUROPEAN_SCOUT   — correct on a cross-fantasy player             (+25 b)
 */
export const computeRoundBonuses = ({ player, correct, cluesRevealed, chainState = null }) => {
    const out = [];
    if (!correct) return out;

    // Streak-driven bonuses when the caller supplies chain state.
    if (chainState) {
        if (cluesRevealed <= 1) {
            const newLen = (chainState.firstClueChain || 0) + 1;
            const points = computeStreakBonus("first_clue_chain", newLen);
            if (points > 0) {
                out.push({
                    id: "first_clue_chain",
                    label: `INKO Vision × ${newLen}`,
                    points,
                    icon: "eye",
                });
            }
        } else if (cluesRevealed <= 2) {
            const newLen = (chainState.earlyChain || 0) + 1;
            const points = computeStreakBonus("early_chain", newLen);
            if (points > 0) {
                out.push({
                    id: "early_chain",
                    label: `Early Chain × ${newLen}`,
                    points,
                    icon: "lightning",
                });
            }
        }
    } else {
        // Backward-compat: flat bonuses when caller has no chain context.
        if (cluesRevealed <= 1) {
            out.push({ id: "inko_vision", label: "INKO Vision", points: 50, icon: "eye" });
        } else if (cluesRevealed <= 3) {
            out.push({ id: "early_chain", label: "Early Chain", points: 30, icon: "lightning" });
        }
    }

    // Entity-type bonuses (always fire when applicable).
    if (isManager(player)) {
        out.push({ id: "touchline_snipe", label: "Touchline Snipe", points: 100, icon: "target" });
    }
    if (isPain(player)) {
        const points = player.severity === "legendary" ? 50 : 30;
        out.push({ id: "death_mode_survivor", label: "Death Mode Survivor", points, icon: "flag" });
    }
    if (!isManager(player) && !isPain(player) && getCrossFantasyProfile(player)) {
        out.push({ id: "european_scout", label: "European Scout", points: 25, icon: "globe" });
    }
    return out;
};

/**
 * Update the streak chain state after a round result. Called by the parent
 * game controller BEFORE showing the next round.
 *
 * Rules:
 *   • correct on clue #1  → firstClueChain += 1, earlyChain += 1
 *   • correct on clue #2  → firstClueChain resets, earlyChain += 1
 *   • otherwise           → both reset to 0
 */
export const advanceChainState = (chainState, { correct, cluesRevealed }) => {
    const s = { earlyChain: 0, firstClueChain: 0, ...(chainState || {}) };
    if (!correct) return { earlyChain: 0, firstClueChain: 0 };
    if (cluesRevealed <= 1) {
        return { earlyChain: (s.earlyChain || 0) + 1, firstClueChain: (s.firstClueChain || 0) + 1 };
    }
    if (cluesRevealed <= 2) {
        return { earlyChain: (s.earlyChain || 0) + 1, firstClueChain: 0 };
    }
    return { earlyChain: 0, firstClueChain: 0 };
};

/**
 * End-of-game bonuses computed from the whole `results[]` array (each item
 * is a `{ player, correct, cluesRevealed, hintUsed, ... }`).
 *
 * Graduated no-miss bonus (highest qualifying tier only):
 *   • 10/10 correct                           → +200 b (FPL Oracle)
 *   • 10/10 correct AND all by clue ≤ 3      → +350 b (Oracle Elite)
 *   • 10/10 correct AND all by clue ≤ 2      → +500 b (Oracle God)
 *
 * Additional flat:
 *   • NO_HINTS — 0 paid hints across the game → +50 b
 *
 * Only applied when the game is fully played (`results.length >= 10`).
 */
export const computeEndGameBonuses = (results) => {
    const out = [];
    if (!Array.isArray(results) || results.length < 10) return out;
    const correctCount = results.filter((r) => r.correct).length;
    const anyHintUsed = results.some((r) => r.hintUsed);
    if (correctCount === results.length) {
        const allByClue2 = results.every((r) => r.cluesRevealed <= 2);
        const allByClue3 = results.every((r) => r.cluesRevealed <= 3);
        if (allByClue2) {
            out.push({ id: "oracle_god", label: "Oracle God", points: 500, icon: "trophy" });
        } else if (allByClue3) {
            out.push({ id: "oracle_elite", label: "Oracle Elite", points: 350, icon: "trophy" });
        } else {
            out.push({ id: "fpl_oracle", label: "FPL Oracle", points: 200, icon: "trophy" });
        }
    }
    if (!anyHintUsed) {
        out.push({ id: "no_hints", label: "No Hints", points: 50, icon: "brain" });
    }
    return out;
};

/**
 * Compute the badges / titles awarded at the end of a game. Titles are
 * cosmetic (no points) — they show up on the Result Screen and are meant
 * for social/share flex. Each title comes with an `id`, `label`, and
 * short Czech `desc`.
 */
export const computeTitles = (results, totalScore) => {
    const out = [];
    if (!Array.isArray(results) || results.length === 0) return out;
    const correctCount = results.filter((r) => r.correct).length;
    const clue1Hits = results.filter((r) => r.correct && r.cluesRevealed <= 1).length;
    const earlyHits = results.filter((r) => r.correct && r.cluesRevealed <= 2).length;
    const managerHit = results.some((r) => r.correct && isManager(r.player));
    const painHits = results.filter((r) => r.correct && isPain(r.player)).length;
    const cfHits = results.filter(
        (r) => r.correct && !isManager(r.player) && !isPain(r.player) && getCrossFantasyProfile(r.player)
    ).length;
    const anyHintUsed = results.some((r) => r.hintUsed);
    const isFullGame = results.length >= 10;

    if (correctCount === results.length && isFullGame) {
        out.push({ id: "clean_sheet", label: "Clean Sheet", desc: "10/10 správně" });
        if (totalScore >= 900) {
            out.push({ id: "fpl_oracle", label: "FPL Oracle", desc: "Elite skóre + 10/10" });
        }
    }
    if (clue1Hits >= 3) {
        out.push({ id: "inko_vision", label: "INKO Vision", desc: `${clue1Hits}× trefa na indícii #1` });
    }
    if (earlyHits >= 5) {
        out.push({ id: "early_bird", label: "Early Bird", desc: `${earlyHits}× trefa do #2 indície` });
    }
    if (managerHit) {
        out.push({ id: "touchline_sniper", label: "Touchline Sniper", desc: "Sejmul rare managera" });
    }
    if (painHits >= 1) {
        out.push({
            id: "death_mode_survivor",
            label: "Death Mode Survivor",
            desc: `${painHits}× přežil FPL Pain moment`,
        });
    }
    if (cfHits >= 2) {
        out.push({
            id: "european_scout",
            label: "European Scout",
            desc: `${cfHits}× cross-fantasy trefa`,
        });
    }
    if (totalScore >= 800) {
        out.push({ id: "budget_genius", label: "Budget Genius", desc: `${totalScore} b — high roller` });
    }
    if (!anyHintUsed && isFullGame) {
        out.push({ id: "no_hints", label: "No Hints", desc: "Nula placených nápověd" });
    }
    return out;
};

/**
 * Given a base pool and a manager pool, roll a `spawn_probability` chance to
 * inject exactly ONE manager into the base pool, replacing a random slot.
 *
 * Rules per the design brief:
 *   • max 1 manager per 10-round game
 *   • spawn_probability defaults to 0.02 → ~2 % of games contain a manager
 *   • deterministic when `seedKey` provided (Daily 10 mode gets same manager
 *     for the same date so all users share the outcome)
 */
export const maybeInjectManager = (pool, managers, opts = {}) => {
    const spawnProbability = opts.spawnProbability ?? 0.02;
    const seedKey = opts.seedKey || null;

    let rand;
    if (seedKey) {
        const s = hashString(String(seedKey));
        rand = mulberry32(s);
    } else {
        rand = Math.random;
    }
    if (rand() >= spawnProbability) return pool;
    if (!Array.isArray(managers) || managers.length === 0) return pool;
    const mgr = managers[Math.floor(rand() * managers.length)];
    const slot = Math.floor(rand() * pool.length);
    return pool.map((p, i) => (i === slot ? mgr : p));
};

// ---- FPL Pain (Death Mode) --------------------------------------------

/**
 * Field-label lookup for auto-generated Death Mode sentence clues. Keeps
 * the classic/middle reading-mode narrative readable ("Klub v tu chvíli:
 * Watford.") without duplicating strings across the JSON dataset.
 */
const PAIN_FIELD_LABELS = {
    pain_gw:         "FPL Death Mode moment",
    pain_season:     "Sezona",
    pain_club:       "Klub v tu chvíli",
    pain_score:      "FPL skóre",
    pain_event:      "Klíčová událost",
    pain_event2:     "A ještě",
    pain_opponent:   "Protivník",
    pain_scoreline:  "Zápas skončil",
    pain_role:       "Situace",
    name_hint:       "Lehká indicie: příjmení",
};

/**
 * Turn a Pain entry from `inko-pain.json` into a fully playable INKO entity
 * — synthesises `clues[]` (long-text sentences for classic/middle reading
 * mode) from the entry's `micro_clues` and `pain_moment` metadata so we
 * don't have to hand-write them in the JSON.
 *
 * Also tags the entity with `question_type: "fpl_pain"` and the severity
 * multiplier that the scoring pipeline (Phase D) will consume.
 */
export const enrichPainEntity = (pain) => {
    if (!pain) return null;
    const microClues = Array.isArray(pain.micro_clues) ? pain.micro_clues : [];
    const clues = microClues.map((mc, i) => {
        const label = PAIN_FIELD_LABELS[mc.field] || "Death Mode fakt";
        return {
            order: i + 1,
            text: `${label}: ${mc.value}.`,
        };
    });
    // Ensure at least 6 clues so the timer/score table lines up.
    while (clues.length < 6) {
        clues.push({ order: clues.length + 1, text: "···" });
    }
    return {
        // Base identity mirrors regular players so all existing helpers work.
        id: pain.id,
        answer: pain.answer,
        name: pain.answer,
        aliases: pain.aliases || [],
        era: pain.pain_moment?.season || null,
        position_hint: null,
        difficulty: pain.severity === "legendary" ? "hard" : "medium",
        entity_type: "player",       // still a guessable player
        question_type: "fpl_pain",    // discriminator for badges + scoring
        severity: pain.severity || "medium",
        pain_type: pain.pain_type || null,
        pain_moment: pain.pain_moment || null,
        story: pain.story || null,
        clues,
        micro_clues: microClues,
        moment_clues: [], // porno mode falls back to regular clues
        tags: ["fpl_pain", pain.pain_type].filter(Boolean),
    };
};

export const isPain = (player) => player && player.question_type === "fpl_pain";

/**
 * Pick 10 entities for a round using the design-brief weighted mix:
 *   • rare_manager  — 2 %, max 1 per round
 *   • fpl_pain      — 5 %, max 2 per round
 *   • cross_fantasy — 3 %, max 2 per round (a subset of classic_player)
 *   • classic_player — remainder (~90 % effectively)
 *
 * `basePlayers` MUST already be the 260-player pool. `managers` and
 * `painEntries` are optional — when omitted, only classic players are
 * sampled.
 *
 * Accepts optional `seedKey` for deterministic pools (Daily-10 shared
 * pool across users on the same date).
 */
export const pickRoundEntities = (basePlayers, managers = [], painEntries = [], opts = {}) => {
    const {
        seedKey = null,
        rounds = 10,
        managerProb = 0.02,
        painProb = 0.05,
        crossFantasyProb = 0.03,
        maxManagers = 1,
        maxPain = 2,
        maxCrossFantasy = 2,
    } = opts;

    const rand = seedKey ? mulberry32(hashString(String(seedKey))) : Math.random;

    const crossFantasyPool = basePlayers.filter((p) => getCrossFantasyProfile(p));
    const nonCrossFantasyPool = basePlayers.filter((p) => !getCrossFantasyProfile(p));
    const enrichedPain = painEntries.map(enrichPainEntity).filter(Boolean);

    const picked = [];
    const usedIds = new Set();

    let mgrCount = 0;
    let painCount = 0;
    let cfCount = 0;

    const pickUnique = (pool) => {
        if (!Array.isArray(pool) || pool.length === 0) return null;
        for (let attempt = 0; attempt < 20; attempt++) {
            const cand = pool[Math.floor(rand() * pool.length)];
            const id = cand.id || cand.answer;
            if (!usedIds.has(id)) {
                usedIds.add(id);
                return cand;
            }
        }
        return pool[Math.floor(rand() * pool.length)];
    };

    for (let i = 0; i < rounds; i++) {
        const roll = rand();
        let cand = null;
        // Bucketed weighted roll: mgr → pain → cf → classic. Each bucket
        // respects its per-round cap; overflow falls through to the next.
        if (mgrCount < maxManagers && roll < managerProb && managers.length > 0) {
            cand = pickUnique(managers);
            if (cand) mgrCount++;
        } else if (painCount < maxPain && roll < managerProb + painProb && enrichedPain.length > 0) {
            cand = pickUnique(enrichedPain);
            if (cand) painCount++;
        } else if (cfCount < maxCrossFantasy && roll < managerProb + painProb + crossFantasyProb && crossFantasyPool.length > 0) {
            cand = pickUnique(crossFantasyPool);
            if (cand) cfCount++;
        }
        if (!cand) {
            // Classic bucket. Once we hit maxCrossFantasy, filter CF players
            // out of the classic pool so we don't accidentally exceed the cap.
            const classicPool = cfCount >= maxCrossFantasy ? nonCrossFantasyPool : basePlayers;
            cand = pickUnique(classicPool);
            // Track incidental CF hits from the classic pool.
            if (cand && getCrossFantasyProfile(cand)) cfCount++;
        }
        if (cand) picked.push(cand);
    }
    return picked;
};

// ---- Surname hint (Thunder mode name_hint chip) -----------------------

/**
 * Mask the middle letters of a word, keeping first & last letter visible.
 * "Salah"   → "S___h"
 * "Son"     → "S_n"
 * "Bruyne"  → "B____e"
 * Words shorter than 3 chars are returned unchanged.
 */
const maskWord = (w) => {
    if (!w) return "";
    const len = w.length;
    if (len <= 2) return w;
    if (len === 3) return w[0] + "_" + w[2];
    return w[0] + "_".repeat(len - 2) + w[len - 1];
};

/**
 * Turn a surname string into a "staged hint" that reveals structure without
 * giving the full answer away.
 *   • "Salah"             → "S___h"
 *   • "Son"               → "S_n"
 *   • "De Bruyne"         → "De B____e"     (particle kept intact)
 *   • "Van Dijk"          → "Van D__k"
 *   • "Alexander-Arnold"  → "A_______r-A____d" (each hyphen segment masked)
 *   • "Heung-min"         → "H___g-m_n"
 */
export const buildSurnameHint = (surname) => {
    if (!surname) return "···";
    // Space-separated (De Bruyne, Van Dijk) — keep all prefix parts, mask only the last.
    if (surname.includes(" ")) {
        const parts = surname.split(/\s+/);
        return parts
            .map((p, i) => (i < parts.length - 1 ? p : maskWord(p)))
            .join(" ");
    }
    // Hyphenated (Alexander-Arnold, Heung-min) — mask each part.
    if (surname.includes("-")) {
        return surname.split("-").map(maskWord).join("-");
    }
    return maskWord(surname);
};

/**
 * Resolve the surname-hint chip for a player.
 * Priority order:
 *   1. Curated `player.surname_clue_pool` array — picks a deterministic entry
 *      per player.id (skips the full-name entry to preserve challenge).
 *   2. Algorithmic mask over the name_hint micro_clue value.
 *
 * Used only for chip rendering in Thunder mode when field === "name_hint".
 */
export const surnameHintFor = (player, surname) => {
    if (!player) return buildSurnameHint(surname);
    const pool = player.surname_clue_pool;
    if (Array.isArray(pool) && pool.length > 0) {
        const surnameFull = surname || "";
        const candidates = pool.filter(
            (v) => v && v !== surnameFull && v !== player.name && v !== player.answer
        );
        if (candidates.length > 0) {
            const seed = hashString(String(player.id || player.name || ""));
            return String(candidates[seed % candidates.length]);
        }
    }
    return buildSurnameHint(surname);
};

// ---- PORNO reading mode -----------------------------------------------

/**
 * Cost of the paid "Nápověda příjmení" during a round. Player buys once per
 * round, subtracts these points from whatever they'd otherwise earn (or lose
 * against a 0 baseline on skip / wrong answer).
 *   • easy / medium: -20 points
 *   • hard:          -15 points (fuzzy typing is already harder, so cheaper)
 */
export const paidSurnameHintCost = (mode) => (mode === "hard" ? 15 : 20);

/**
 * Minimum clue reveal count before the paid hint can be purchased. Design
 * choice: the hint is a late-round life-line, not an opening move — you
 * should have already tried to read the FPL context before buying letters.
 */
export const PAID_HINT_MIN_CLUE = 3;

/**
 * Return a masked surname string suitable for the paid hint UI. Draws from
 * `generateSurnameVariants()` (8+ patterns: `5 písmen`, `S____`, `____h`,
 * `S___h`, `a-a`, `Slh`, `A-A` initials, `Spojovník`, `Předložka „De"` …)
 * — picks deterministically per player.id so the same player always yields
 * the same variant for leaderboard fairness, but different players get
 * different patterns.
 *
 * Falls back to curated `player.surname_clue_pool` if the future dataset
 * ships one; then to the plain `first+last` mask.
 */
export const paidSurnameHint = (player) => {
    if (!player) return "";
    // Curated pool wins (future dataset).
    const pool = player.surname_clue_pool;
    if (Array.isArray(pool) && pool.length > 0) {
        const surname = String(player.answer || player.name || "")
            .split(/\s+/)
            .slice(-1)[0] || "";
        const candidates = pool.filter(
            (v) => v && v !== surname && v !== player.name && v !== player.answer
        );
        if (candidates.length > 0) {
            const seed = hashString(String(player.id || player.name || ""));
            return String(candidates[seed % candidates.length]);
        }
    }
    const surname = player.surname || (
        String(player.answer || player.name || "")
            .split(/\s+/)
            .slice(-1)[0] || ""
    );
    const variants = generateSurnameVariants(surname);
    if (variants.length === 0) return buildSurnameHint(surname);
    // Deterministic pick per player.id so refreshing doesn't churn variants.
    const seed = hashString(String(player.id || player.name || surname));
    return variants[seed % variants.length].value;
};

/**
 * Escape a string for use in a RegExp. Used to blank out the player name
 * from moment_clue text (some story-type clues include the name directly).
 */
const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Strip any occurrence of the player's name / aliases / surname from a piece
 * of clue text so the "PORNO" reveal doesn't leak the answer.
 * Case-insensitive, diacritics-insensitive-ish via straight literal match on
 * both the original and normalized forms.
 */
const stripPlayerName = (text, player) => {
    if (!text || !player) return text;
    const names = new Set();
    if (player.name) names.add(player.name);
    if (player.answer) names.add(player.answer);
    for (const a of player.aliases || []) names.add(a);
    // Add each individual token >= 3 chars from the answer (e.g. "Mohamed", "Salah")
    for (const raw of [player.answer, player.name, ...(player.aliases || [])]) {
        if (!raw) continue;
        for (const tok of raw.split(/\s+/)) {
            if (tok.length >= 3) names.add(tok);
        }
    }
    // Sort longest → shortest so multi-word matches happen before their tokens.
    const sorted = [...names].sort((a, b) => b.length - a.length);
    let out = text;
    for (const n of sorted) {
        const re = new RegExp(escapeRegex(n), "gi");
        // Nicer mask: keep first & last letter of the matched token so text
        // stays readable and dashes reveal the length ("Bowen" → "B___n"),
        // instead of a flat "•••" that hides everything and looks broken.
        out = out.replace(re, (match) => maskWord(match));
    }
    return out;
};

/**
 * Build the 6-clue sequence for PORNO reading mode.
 *
 * Uses `player.moment_clues` (hand-crafted from Vaastav historical FPL data:
 * specific GW / points / goals / assists / bonus). Skips `story`-type items
 * whose text often embeds the player's own name; keeps `moment`-type items
 * which reference concrete stats.
 *
 * If fewer than 6 moment items remain, pads with the tail of `player.clues`
 * (regular sentence clues, ordered easier at the end) so we always end at
 * six clues and the scoring table stays intact.
 *
 * Every returned text is passed through `stripPlayerName` to defend against
 * leaks. Returned items conform to the {order, type, text} shape used by the
 * renderer.
 */
export const getPornoClues = (player) => {
    if (!player) return [];
    const raw = Array.isArray(player.moment_clues) ? player.moment_clues : [];
    const moments = raw
        .filter((m) => m && m.text)
        .filter((m) => m.type !== "story");
    const seen = new Set();
    const cleaned = [];
    for (const m of moments) {
        const t = stripPlayerName(m.text, player).trim();
        if (!t || seen.has(t)) continue;
        seen.add(t);
        cleaned.push({ order: cleaned.length + 1, type: "porno", text: t });
    }
    if (cleaned.length >= 6) return cleaned.slice(0, 6).map((c, i) => ({ ...c, order: i + 1 }));

    // Pad with the tail of regular clues (easier-first when read in reverse).
    const regular = [...(player.clues || [])].sort((a, b) => a.order - b.order);
    // Meta-clues describing the *name itself* ("Lehká indicie: příjmení Bowen",
    // "iniciály MS", "křestní jméno Kai" etc.) are terrible in PORNO mode —
    // after masking they collapse to "…B___n" which just spells the answer.
    // Filter them out entirely from the pad pool.
    const NAME_META_RE = /(lehká indicie|příjmení|křestní jméno|jednoslovné jméno|iniciály|zkratka|přezdívka|dvojité příjmení|dvojí jméno|italské příjmení|jméno začíná|začíná na)/i;
    // From the end of the array (easiest first) so pads reveal easier facts as
    // the round progresses.
    const padSource = regular.slice().reverse().filter((c) => !NAME_META_RE.test(c.text || ""));
    for (const c of padSource) {
        if (cleaned.length >= 6) break;
        const t = stripPlayerName(c.text, player).trim();
        if (!t || seen.has(t)) continue;
        seen.add(t);
        cleaned.push({
            order: cleaned.length + 1,
            type: "porno-pad",
            text: t,
        });
    }
    // If we STILL don't have 6 clues (very rare for enriched players), just
    // return what we have — an honest short round is better than a padded
    // "···" placeholder that gives no info and looks broken.
    return cleaned.map((c, i) => ({ ...c, order: i + 1 }));
};

// ---- Micro-clue field labels (THUNDER mode) --------------------------

/**
 * Human-readable Czech labels for the `field` key of `player.micro_clues[]`.
 * Used by THUNDER mode to render `{label · value}` chips.
 */
export const MICRO_CLUE_LABELS = {
    // Standard schema (200 core players)
    price_tier:  "Cena",
    peak_season: "Sezóna",
    points_band: "Body",
    position:    "Pozice",
    club:        "Klub",
    name_hint:   "Jméno",
    // Legacy schema (60 enablers)
    hint:        "Jméno",
    price:       "Cena",
    role:        "Role",
    season:      "Sezóna",
    team:        "Klub",
    oldclub:     "Dřívější klub",
    country:     "Země",
    coach:       "Trenér",
    academy:     "Akademie",
    cult:        "Kult",
    foot:        "Noha",
    risk:        "Riziko",
    story:       "Příběh",
};

// Fields whose value is the player's surname (or reveals it) → get masked
// through buildSurnameHint() in Thunder mode chip render.
export const SURNAME_LEAK_FIELDS = new Set(["name_hint", "hint"]);

/**
 * Resolve the raw surname string for a player, in priority order:
 *   1. explicit `player.surname` (future curated dataset)
 *   2. `name_hint` / `hint` micro_clue value
 *   3. last space-separated token of answer / name (best-effort)
 */
const surnameFromPlayer = (player) => {
    if (!player) return "";
    if (player.surname) return String(player.surname);
    const micro = (player.micro_clues || []).find((m) =>
        SURNAME_LEAK_FIELDS.has(m.field)
    );
    if (micro?.value) return String(micro.value);
    return String(player.answer || player.name || "")
        .split(/\s+/)
        .slice(-1)[0] || "";
};

const VOWEL_RE = /[aeiouyáéíóúůýěäöüàèìòùâêîôûãõñ]/i;

/**
 * Generate a rich pool of surname clue *variants* algorithmically. Each is a
 * `{type, value}` where `type` is a category (used for round-level dedup) and
 * `value` is the display string shown to the player.
 *
 * Types produced (up to 10 per player):
 *   letters       → "5 písmen"
 *   first-only    → "S____"
 *   last-only     → "____h"
 *   first-last    → "S___h"      (default best hint)
 *   vowels        → "a-a"
 *   consonants    → "Slh"
 *   hyphen        → "Spojovník"       (only if surname has "-")
 *   initials      → "A-A"             (only if surname has "-")
 *   parts         → "2 části"         (only if surname has " ")
 *   prefix        → "Předložka „De\"" (only if surname starts with prefix)
 */
export const generateSurnameVariants = (surname) => {
    if (!surname) return [];
    const variants = [];
    const clean = surname.replace(/[-\s]/g, "");
    const len = clean.length;
    if (len === 0) return variants;

    variants.push({ type: "letters", value: `${len} písmen` });
    if (len >= 3) {
        variants.push({
            type: "first-only",
            value: clean[0] + "_".repeat(Math.min(len - 1, 6)),
        });
        variants.push({
            type: "last-only",
            value: "_".repeat(Math.min(len - 1, 6)) + clean[len - 1],
        });
    }
    variants.push({ type: "first-last", value: buildSurnameHint(surname) });

    const vowels = clean.match(new RegExp(VOWEL_RE, "gi"));
    if (vowels && vowels.length > 0) {
        variants.push({ type: "vowels", value: vowels.join("-").toLowerCase() });
    }
    const consonants = clean.replace(new RegExp(VOWEL_RE, "gi"), "");
    if (consonants.length >= 2 && consonants.length < len) {
        variants.push({ type: "consonants", value: consonants });
    }

    if (surname.includes("-")) {
        variants.push({ type: "hyphen", value: "Spojovník" });
        const initials = surname.split("-").map((p) => p[0]).join("-");
        variants.push({ type: "initials", value: initials });
    }
    if (surname.includes(" ")) {
        const parts = surname.split(/\s+/);
        variants.push({ type: "parts", value: `${parts.length} části` });
        const prefixes = ["Van", "De", "Von", "Del", "Der", "Al", "Da"];
        if (prefixes.includes(parts[0])) {
            variants.push({ type: "prefix", value: `Předložka „${parts[0]}"` });
        }
    }
    return variants;
};

/**
 * Round-level surname hint picker with type-level dedup across the whole
 * round. Ensures the same variant type ("5 písmen", "vowels", "hyphen"…)
 * appears at most ONCE per game — so players can't guess by process of
 * elimination through recurring patterns.
 *
 * Priority:
 *   1. Curated `player.surname_clue_pool` (skips full-name entries).
 *   2. Algorithmic variants — pick from types not yet used, deterministic
 *      per `seedKey` (e.g. Daily-10 uses `daily10:<date>`).
 *
 * Returns a Map<playerId, hintString> so chip render can look up by id.
 */
export const pickRoundSurnameHints = (players, seedKey = "") => {
    const usedTypes = new Set();
    const map = new Map();
    if (!Array.isArray(players) || players.length === 0) return map;
    const rng = mulberry32(hashString(String(seedKey || "inko-round")));

    for (const p of players) {
        const surname = surnameFromPlayer(p);
        // Curated pool wins (future dataset). Skip full-name entries.
        const curated = Array.isArray(p.surname_clue_pool) ? p.surname_clue_pool : null;
        if (curated && curated.length > 0) {
            const filtered = curated.filter(
                (v) => v && v !== surname && v !== p.answer && v !== p.name
            );
            if (filtered.length > 0) {
                const val = filtered[Math.floor(rng() * filtered.length)];
                // Use the value string itself as the "type" for curated entries
                // so identical values still dedup across the round.
                if (!usedTypes.has(val)) {
                    usedTypes.add(val);
                    map.set(p.id, val);
                    continue;
                }
            }
        }
        const variants = generateSurnameVariants(surname);
        const unused = variants.filter((v) => !usedTypes.has(v.type));
        const pool = unused.length > 0 ? unused : variants;
        if (pool.length > 0) {
            const picked = pool[Math.floor(rng() * pool.length)];
            usedTypes.add(picked.type);
            map.set(p.id, picked.value);
        }
    }
    return map;
};

/**
 * For a given clue order (1-6), return the matching micro_clue from the player
 * (or null if the dataset is missing it — old cached data).
 */
export const microClueFor = (player, clueOrder) => {
    if (!player || !Array.isArray(player.micro_clues)) return null;
    return player.micro_clues.find((m) => m.order === clueOrder) || null;
};

/**
 * Reorder a player's `micro_clues` so the same field (e.g. price_tier / CENA)
 * doesn't always land on position #1. Keeps the hard→easy progression by
 * shuffling only *within* three difficulty tiers:
 *   • Hard tier   (positions 1-3): price_tier, peak_season, points_band
 *   • Medium tier (positions 4-5): position, club
 *   • Easy        (position 6):    name_hint
 *
 * Seeded by `seedKey` (typically player.id) so the order is deterministic
 * per player — same player always shows the same permutation, which is
 * required for a fair Daily Challenge leaderboard. Different players get
 * different orders.
 *
 * Returns a fresh array of micro_clues with rewritten `order` values (1-6);
 * originals are untouched. Falls back to the raw list if fewer than 6 items.
 */
export const shuffleMicroClues = (microClues, seedKey = "") => {
    if (!Array.isArray(microClues) || microClues.length < 3) return microClues || [];
    const byField = {};
    for (const m of microClues) byField[m.field] = m;

    const seed = hashString(String(seedKey) || "inko");

    // Label-family de-dup: prevents two "Cena" chips when a player has BOTH
    // price_tier and price micro_clues (~39 legacy enablers).
    const labelOf = (m) => MICRO_CLUE_LABELS[m.field] || m.field;

    const pick = (candidates, usedLabels) => {
        for (const f of candidates) {
            const m = byField[f];
            if (m && !usedLabels.has(labelOf(m))) {
                usedLabels.add(labelOf(m));
                return m;
            }
        }
        return null;
    };

    const usedLabels = new Set();
    const hard = [
        pick(["price_tier", "price"], usedLabels),
        pick(["peak_season", "season"], usedLabels),
        pick(["points_band"], usedLabels),
    ].filter(Boolean);
    const mid = [
        pick(["position", "role"], usedLabels),
        pick(["club", "team", "oldclub"], usedLabels),
    ].filter(Boolean);
    const easy = [pick(["name_hint", "hint"], usedLabels)].filter(Boolean);

    const shuffledHard = shuffleArrSeeded(hard, seed);
    const shuffledMid = shuffleArrSeeded(mid, seed ^ 0x9E3779B9);

    // Extras — leftover fields with UNIQUE labels (country / coach / risk …).
    const usedObjs = new Set([...shuffledHard, ...shuffledMid, ...easy]);
    const extras = [];
    for (const m of microClues) {
        if (usedObjs.has(m)) continue;
        const lbl = labelOf(m);
        if (usedLabels.has(lbl)) continue;
        usedLabels.add(lbl);
        extras.push(m);
    }
    const shuffledExtras = shuffleArrSeeded(extras, seed ^ 0x2C1D4E6F);

    // Compose: hard(1-3) + mid(4-5) + easy(6). Borrow from extras to hit 5
    // pre-easy slots when standard buckets are short.
    let seq = [...shuffledHard, ...shuffledMid];
    while (seq.length < 5 && shuffledExtras.length > 0) {
        seq.push(shuffledExtras.shift());
    }
    seq = seq.slice(0, 5);
    const easyChip = easy[0] || shuffledExtras[0] || null;
    const composed = easyChip ? [...seq, easyChip] : seq;
    return composed.map((m, i) => ({ ...m, order: i + 1 }));
};
